import { useCallback, useEffect, useMemo, useState } from "react";
import { IDLE } from "@/features/identity/hooks/useSectionForm";
import { useIdentity } from "@/features/identity/hooks/useIdentity";
import { useMyLinks } from "@/features/identity/hooks/useMyLinks";
import { buildStructuredIdentity } from "@/features/identity/buildStructuredIdentity";
import { getMyPrimaryCard, findPublicProfileForCard } from "@/features/cards/api/cardsApi";
import { saveMyProfileTheme } from "@/features/profiles/api/myPublicProfileApi";
import { generateTheme, normalizeHex, DEFAULT_THEME_COLOR } from "@/lib/theme/generateTheme";
import { getCanonicalProfileUrl } from "@/lib/canonicalUrl";
import { friendlyDataError } from "@/lib/errors";

const DEFAULT = DEFAULT_THEME_COLOR.toLowerCase();

export function useProfileTheme() {
  const { profile } = useIdentity();
  const links = useMyLinks();
  const [load, setLoad] = useState({ status: "loading" });
  const [color, setColor] = useState(DEFAULT);
  const [saved, setSaved] = useState(DEFAULT);
  const [status, setStatus] = useState(IDLE);

  const fetchAll = useCallback(async () => {
    setLoad({ status: "loading" });
    try {
      const card = await getMyPrimaryCard();
      const pp = card ? await findPublicProfileForCard(card.id) : null;
      const initial = normalizeHex(pp?.design_config?.themeColor) || DEFAULT;
      setColor(initial);
      setSaved(initial);
      setLoad({ status: "ready", card, pp });
    } catch (err) {
      setLoad({ status: "error", error: friendlyDataError(err, "We couldn't load your profile settings.") });
    }
  }, []);
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const save = async () => {
    setStatus({ state: "saving" });
    try {
      const pp = await saveMyProfileTheme(load.card, load.pp, color);
      setLoad((l) => ({ ...l, pp }));
      setSaved(color);
      setStatus({ state: "saved", message: "Theme saved." });
      return true;
    } catch (err) {
      setStatus({ state: "error", message: friendlyDataError(err, "We couldn't save your theme. Please try again.") });
      return false;
    }
  };
  const change = (c) => {
    setColor(c);
    setStatus(IDLE);
  };

  const slug = load.pp?.slug || load.card?.slug;
  const identity = useMemo(() => buildStructuredIdentity(profile, links.links, { canonicalUrl: getCanonicalProfileUrl(slug) }), [profile, links.links, slug]);
  const theme = useMemo(() => generateTheme(color), [color]);

  return { load, color, dirty: color !== saved, status, change, save, reload: fetchAll, slug, identity, theme };
}
